import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { FieldHighlightRect } from '../services/types';
import { BRRApiClient } from '../services/api';

interface PdfHighlightModalProps {
  apiClient: BRRApiClient;
  blobName: string;
  highlightPage?: number;
  highlightRects?: FieldHighlightRect[];
  matchType?: string;
  fieldName?: string;
  matchedText?: string;
  onClose: () => void;
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export const PdfHighlightModal: React.FC<PdfHighlightModalProps> = ({
  apiClient,
  blobName,
  highlightPage = 0,
  highlightRects = [],
  matchType = '',
  fieldName = 'Document Preview',
  matchedText = '',
  onClose,
}) => {
  const isScanned = matchType === 'llm_vision';
  const isPreviewOnly = !matchedText;
  const [currentPage, setCurrentPage] = useState(highlightPage);
  const [totalPages, setTotalPages] = useState(1);
  const [zoomIdx, setZoomIdx] = useState(2); // index 2 = 1x (100%)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const zoom = ZOOM_LEVELS[zoomIdx];

  // Lock body scroll while modal is open, preserving scroll position
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiClient.getDocumentPageCount(blobName).then((count) => {
      if (cancelled) return;
      setTotalPages(count);
      if (highlightPage >= count) {
        setCurrentPage(0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apiClient, blobName, highlightPage]);

  // Rects are drawn on the highlight page only
  const showRects = currentPage === highlightPage;

  // The page image is fetched rather than linked because the endpoint requires a
  // bearer token, which an `<img src>` cannot carry. The object URL is revoked on
  // every page change so the blobs don't accumulate for the tab's lifetime.
  // highlightRects is deliberately not a dependency: callers pass a fresh array
  // literal on every render, which would refetch the image in a loop.
  // Both flags are reset here rather than at the call sites that change the page:
  // the error message replaces the `<img>`, so a latched error would leave no
  // element whose onLoad could ever clear it again.
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    const rects = showRects ? highlightRects : [];
    setLoading(true);
    setError(false);

    const load = async () => {
      try {
        const blob = await apiClient.getDocumentHighlightImage(blobName, currentPage, rects);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch {
        if (cancelled) return;
        setLoading(false);
        setError(true);
      }
    };
    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiClient, blobName, currentPage, showRects]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const zoomIn = useCallback(() => {
    if (zoomIdx < ZOOM_LEVELS.length - 1) setZoomIdx(zoomIdx + 1);
  }, [zoomIdx]);

  const zoomOut = useCallback(() => {
    if (zoomIdx > 0) setZoomIdx(zoomIdx - 1);
  }, [zoomIdx]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">{fieldName}</h3>
            {!isPreviewOnly && (
              <p className="text-xs text-gray-500 mt-0.5">
                Matched: &quot;{matchedText}&quot;
              </p>
            )}
            {isScanned && (
              <p className="text-xs text-gray-400 mt-1">
                This is a scanned document. The highlighted area is approximate.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors ml-2"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Toolbar: page nav + zoom */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50 text-sm">
          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToPrevPage}
              disabled={currentPage <= 0}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronDown size={16} className="rotate-90" />
            </button>
            <span className="text-xs text-gray-600 min-w-[80px] text-center">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={goToNextPage}
              disabled={currentPage >= totalPages - 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={zoomOut}
              disabled={zoomIdx <= 0}
              className="px-1.5 py-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-600"
            >
              −
            </button>
            <span className="text-xs text-gray-600 min-w-[40px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={zoomIn}
              disabled={zoomIdx >= ZOOM_LEVELS.length - 1}
              className="px-1.5 py-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-600"
            >
              +
            </button>
          </div>
        </div>

        {/* Image */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50 min-h-[300px]">
          <div className="flex justify-center">
            {loading && !error && (
              <div className="absolute mt-20 flex items-center gap-2 text-gray-500">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-sm">Loading page...</span>
              </div>
            )}
            {error ? (
              <div className="text-sm text-red-500 mt-20">Failed to load page.</div>
            ) : imageUrl && (
              <img
                src={imageUrl}
                alt={`PDF page ${currentPage + 1} for ${fieldName}`}
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top center',
                  display: loading ? 'none' : 'block',
                }}
                onLoad={() => { setLoading(false); setError(false); }}
                onError={() => { setLoading(false); setError(true); }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
